import { LoginScreen, Spinner, TabBar } from './components';
import { ChangelogView } from './components/ChangelogView';
import { ConversationsView } from './components/ConversationsView';
import { CredentialsView } from './components/CredentialsView';
import { TasksView } from './components/TasksView';
import { UsersView } from './components/UsersView';
import { useNavigation } from './hooks/use-navigation';
import { AuthProvider, useAuth } from './providers/auth-provider';
import { TasksProvider } from './providers/tasks-provider';
import { ThemeProvider, useTheme } from './providers/ThemeProvider';

export interface AppProps {
  tenantId?: string | null;
  token?: string | null;
  email?: string | null;
  apiUrl?: string | null;
  type?: string | null;
  dataStyle?: string | null;
  dataTheme?: 'light' | 'dark' | null;
  shadowRoot?: ShadowRoot | null;
}

function AuthenticatedApp() {
  const { theme } = useTheme();
  const { activeTab, setActiveTab } = useNavigation();

  return (
    <div
      data-testid="demo-container"
      className="flex flex-col h-full bg-sd-background"
    >
      <TabBar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 min-h-0">
        <div
          id={`tabpanel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`tab-${activeTab}`}
          className="h-full"
        >
          {activeTab === 'conversations' ? (
            <ConversationsView />
          ) : activeTab === 'tasks' ? (
            <TasksView />
          ) : activeTab === 'credentials' ? (
            <CredentialsView />
          ) : activeTab === 'users' ? (
            <UsersView />
          ) : (
            <ChangelogView />
          )}
        </div>
      </main>
      <span data-testid="theme-indicator" className="sr-only">{theme}</span>
    </div>
  );
}

function AppContent() {
  const { status, configError, externalEmail } = useAuth();

  if (configError) {
    return (
      <div className="flex items-center justify-center h-full bg-sd-background p-sd-4">
        <div className="text-center max-w-sm">
          <p className="text-sd-error text-sd-sm font-medium">{configError}</p>
        </div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center h-full bg-sd-background">
        <Spinner size="lg" />
      </div>
    );
  }

  if (status === 'idle' || status === 'error') {
    return <LoginScreen externalEmail={externalEmail} />;
  }

  return (
    <TasksProvider>
      <AuthenticatedApp />
    </TasksProvider>
  );
}

function App({ dataStyle, dataTheme, shadowRoot, token, email, apiUrl }: AppProps = {}) {
  return (
    <ThemeProvider dataStyle={dataStyle} dataTheme={dataTheme} shadowRoot={shadowRoot}>
      <AuthProvider externalToken={token} externalEmail={email} apiBaseUrl={apiUrl}>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
